
import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserRole } from '@/types/strategy-types';
import { Clipboard, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface UserFormProps {
  onSubmit: (data: {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    password: string;
    sendEmail: boolean;
    forcePasswordChange: boolean;
  }) => void;
  isSubmitting: boolean;
  error?: string | null;
}

export const UserForm = ({ onSubmit, isSubmitting, error }: UserFormProps) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.User);
  const [password, setPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Clear email error when email changes
  useEffect(() => {
    if (emailError) {
      setEmailError(null);
    }
  }, [email]);
  
  // Set email error from prop
  useEffect(() => {
    if (error && error.toLowerCase().includes('email')) {
      setEmailError(error);
    }
  }, [error]);

  // Generate a secure random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+';
    let newPassword = '';
    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPassword);
  };

  // Generate a password on first render
  React.useEffect(() => {
    generatePassword();
  }, []);

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(password).then(() => {
      toast.success("Password copied to clipboard");
    }).catch(err => {
      console.error('Failed to copy password: ', err);
      toast.error("Failed to copy password");
    });
  };

  const validateForm = () => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onSubmit({
      email,
      firstName,
      lastName,
      role,
      password,
      sendEmail,
      forcePasswordChange
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className={emailError ? "text-destructive" : ""}>Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
        />
        {emailError && (
          <p className="text-sm font-medium text-destructive mt-1">{emailError}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
          <SelectTrigger>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UserRole.User}>User</SelectItem>
            <SelectItem value={UserRole.Admin}>Admin</SelectItem>
            <SelectItem value={UserRole.StrategyProvider}>Strategy Provider</SelectItem>
            <SelectItem value={UserRole.Editor}>Editor</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="flex space-x-2">
          <Input
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="flex-1"
          />
          <Button 
            type="button" 
            variant="outline" 
            size="icon"
            onClick={generatePassword}
            title="Generate new password"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            size="icon"
            onClick={copyPasswordToClipboard}
            title="Copy to clipboard"
          >
            <Clipboard className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 pt-2">
        <Switch 
          id="sendEmail" 
          checked={sendEmail}
          onCheckedChange={setSendEmail}
        />
        <Label htmlFor="sendEmail">Send credentials by email</Label>
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch 
          id="forcePasswordChange" 
          checked={forcePasswordChange}
          onCheckedChange={setForcePasswordChange}
        />
        <Label htmlFor="forcePasswordChange">Force password change on first login</Label>
      </div>
      
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Creating User..." : "Create User"}
      </Button>
    </form>
  );
};
